import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState, useCallback, useEffect, type FormEvent } from 'react'
import { Users, Building2, Clock, MessageSquare, Mail, UserPlus, Search, X, Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FilterBuilder, FilterState, FilterableField } from '@/components/filters'
import NavigationRail from '@/components/NavigationRail'
import { formatRelativeTime } from '@/lib/displayUtils'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import type { Agent } from '@/types/shared'

interface Lead {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  lastSeenAt: string | null
  createdAt: string
  conversationsCount: number
}

interface Props {
  leads: Lead[]
  totalCount: number
  filteredCount: number
  usersCount: number
  companiesCount: number
  filterableFields: FilterableField[]
  currentFilters: FilterState
  currentSearch: string | null
  currentAgent: Agent
  hasMore: boolean
  currentPage: number
}

export default function ContactsLeads({
  leads: initialLeads,
  totalCount,
  filteredCount,
  usersCount,
  companiesCount,
  filterableFields,
  currentFilters,
  currentSearch,
  currentAgent,
  hasMore: initialHasMore,
  currentPage: initialCurrentPage,
}: Props) {
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [leads, setLeads] = useState<Lead[]>(initialLeads);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState(currentSearch ?? '');
  const [isSearchOpen, setIsSearchOpen] = useState(!!currentSearch);
  const [isNewLeadOpen, setIsNewLeadOpen] = useState(false);

  const leadForm = useForm({
    name: '',
    email: '',
    phone: '+55 ',
  });

  const formatPhone = useCallback((value: string) => {
    // Strip everything except digits
    const digits = value.replace(/\D/g, '');

    // Always keep +55 prefix
    // If user typed digits, assume first two are country code (55)
    let rest = digits;
    if (digits.startsWith('55')) {
      rest = digits.slice(2);
    }

    if (rest.length === 0) return '+55 ';
    if (rest.length <= 2) return `+55 (${rest}`;
    if (rest.length <= 6) return `+55 (${rest.slice(0, 2)}) ${rest.slice(2)}`;
    if (rest.length <= 10) return `+55 (${rest.slice(0, 2)}) ${rest.slice(2, 6)}-${rest.slice(6)}`;
    // Mobile: 11 digits (2 DDD + 9 digits)
    return `+55 (${rest.slice(0, 2)}) ${rest.slice(2, 7)}-${rest.slice(7, 11)}`;
  }, []);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // If user cleared everything, keep the prefix
    if (raw.replace(/\D/g, '').length <= 2) {
      leadForm.setData('phone', '+55 ');
      return;
    }
    leadForm.setData('phone', formatPhone(raw));
  }, [formatPhone, leadForm]);

  const handleCreateLead = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      leadForm.post('/contacts/create_lead', {
        onSuccess: () => {
          setIsNewLeadOpen(false);
          leadForm.reset();
        },
      });
    },
    [leadForm]
  );

  // Reset state when filters change (server returns new data)
  useEffect(() => {
    setLeads(initialLeads);
    setHasMore(initialHasMore);
    setCurrentPage(initialCurrentPage);
  }, [initialLeads, initialHasMore, initialCurrentPage]);

  useEffect(() => {
    setSearchInput(currentSearch ?? '');
    setIsSearchOpen(!!currentSearch);
  }, [currentSearch]);

  const loadMoreLeads = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const filtersJson = JSON.stringify(filters);
    const searchParam = currentSearch?.trim();

    try {
      const response = await fetch(
        `/contacts/leads/more?page=${nextPage}&filters=${encodeURIComponent(filtersJson)}${
          searchParam ? `&search=${encodeURIComponent(searchParam)}` : ''
        }`
      );
      const data = await response.json();

      setLeads((prev) => [...prev, ...data.leads]);
      setHasMore(data.hasMore);
      setCurrentPage(data.currentPage);
    } catch (error) {
      console.error('Error loading more leads:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentPage, filters, currentSearch]);

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: loadMoreLeads,
  });

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);

    const filtersJson = JSON.stringify(newFilters);
    const searchParam = currentSearch?.trim();
    const url = searchParam
      ? `/contacts/leads?filters=${encodeURIComponent(filtersJson)}&search=${encodeURIComponent(searchParam)}`
      : `/contacts/leads?filters=${encodeURIComponent(filtersJson)}`;

    router.visit(url, {
      preserveState: true,
      preserveScroll: true,
    });
  }, [currentSearch]);

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = searchInput.trim();
      const filtersJson = JSON.stringify(filters);
      const url = trimmed
        ? `/contacts/leads?filters=${encodeURIComponent(filtersJson)}&search=${encodeURIComponent(trimmed)}`
        : `/contacts/leads?filters=${encodeURIComponent(filtersJson)}`;

      router.visit(url, {
        preserveState: true,
        preserveScroll: true,
      });
    },
    [filters, searchInput]
  );

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    const filtersJson = JSON.stringify(filters);
    router.visit(`/contacts/leads?filters=${encodeURIComponent(filtersJson)}`, {
      preserveState: true,
      preserveScroll: true,
    });
  }, [filters]);

  const handleSearchToggle = useCallback(() => {
    if (currentSearch?.trim()) {
      setSearchInput('');
      setIsSearchOpen(false);
      const filtersJson = JSON.stringify(filters);
      router.visit(`/contacts/leads?filters=${encodeURIComponent(filtersJson)}`, {
        preserveState: true,
        preserveScroll: true,
      });
      return;
    }

    setIsSearchOpen((prev) => !prev);
  }, [currentSearch, filters]);

  const isFiltered = !!currentSearch?.trim() || filters.filters.length > 0;

  return (
    <>
      <Head title="Leads - Contatos" />

      <div className="h-screen flex bg-white">
        <NavigationRail currentAgent={currentAgent} activePage="contacts" />

        {/* Sidebar */}
        <div className="w-48 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Contatos</h1>
          </div>

          <nav className="flex-1 p-2">
            <Link
              href="/contacts/users"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <Users className="w-5 h-5 shrink-0" />
              Usuários
              <span className="ml-auto text-xs text-gray-500">{usersCount}</span>
            </Link>
            <Link
              href="/contacts/leads"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg"
            >
              <UserPlus className="w-5 h-5 shrink-0" />
              Leads
              <span className="ml-auto text-xs text-blue-600">{totalCount}</span>
            </Link>
            <Link
              href="/contacts/companies"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <Building2 className="w-5 h-5 shrink-0" />
              Empresas
              <span className="ml-auto text-xs text-gray-500">{companiesCount}</span>
            </Link>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {isFiltered ? (
                  <>
                    {filteredCount.toLocaleString()} leads correspondem de{' '}
                    {totalCount.toLocaleString()}
                  </>
                ) : (
                  <>
                    {totalCount.toLocaleString()} leads
                  </>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {isSearchOpen && (
                  <form onSubmit={handleSearchSubmit} className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="search"
                      autoFocus
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Buscar por nome ou email"
                      className="w-60 pl-9 pr-8 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={handleSearchClear}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Limpar busca"
                        title="Limpar busca"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </form>
                )}
                <button
                  type="button"
                  onClick={handleSearchToggle}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                  aria-label={currentSearch ? 'Limpar busca' : 'Buscar'}
                  title={currentSearch ? 'Limpar busca' : 'Buscar'}
                >
                  <Search className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { leadForm.clearErrors(); setIsNewLeadOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Novo Lead
                </button>
              </div>
            </div>

            <FilterBuilder
              fields={filterableFields}
              value={filters}
              onChange={handleFiltersChange}
            />
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-mail
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Última visita
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversas
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => router.visit(`/contacts/${lead.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-gray-500 text-sm font-medium bg-gray-200">
                          ?
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {lead.name || 'Anônimo'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        {lead.email}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {formatRelativeTime(lead.lastSeenAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {formatRelativeTime(lead.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                        {lead.conversationsCount}
                      </div>
                    </td>
                  </tr>
                ))}

                {leads.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      Nenhum lead encontrado com os filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Infinite scroll sentinel and loading indicator */}
            <div ref={sentinelRef} className="py-4">
              {isLoadingMore && (
                <div className="flex justify-center">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {!hasMore && leads.length > 0 && (
                <p className="text-center text-xs text-gray-400">
                  Todos os leads carregados
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isNewLeadOpen} onOpenChange={(open) => { setIsNewLeadOpen(open); if (!open) { leadForm.reset(); leadForm.clearErrors(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Lead</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateLead} className="space-y-4">
            {(leadForm.errors as Record<string, string>).base && (
              <p className="text-sm text-red-600">{(leadForm.errors as Record<string, string>).base}</p>
            )}

            <div>
              <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                id="lead-name"
                type="text"
                value={leadForm.data.name}
                onChange={(e) => leadForm.setData('name', e.target.value)}
                placeholder="Nome do lead"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="lead-email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mail
              </label>
              <input
                id="lead-email"
                type="email"
                value={leadForm.data.email}
                onChange={(e) => leadForm.setData('email', e.target.value)}
                placeholder="email@exemplo.com"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  leadForm.errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {leadForm.errors.email && (
                <p className="mt-1 text-sm text-red-600">{leadForm.errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="lead-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                id="lead-phone"
                type="tel"
                value={leadForm.data.phone}
                onChange={handlePhoneChange}
                placeholder="+55 (11) 99999-9999"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsNewLeadOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={leadForm.processing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors cursor-pointer"
              >
                {leadForm.processing ? 'Criando...' : 'Criar Lead'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
